import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '@/shared/components/ui/loading-spinner';

interface OnboardingNavigationProps {
  currentStep: string;
  hasSurvey: boolean;
  canNavigateToStep: (step: string) => boolean;
               // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onStepChange: (step: any) => void; // Aceita qualquer tipo para compatibilidade com diferentes hooks
  onBack?: () => void;
  onNext?: () => void;
  isSubmitting?: boolean;
  showBackButton?: boolean;
  nextButtonLabel?: string;
  showFinishAnimation?: boolean; // Para mostrar animação de borda no botão finalizar
  showOrganizationStep?: boolean; // Convidado (já tem org) não passa pela criação de organização
}

export const OnboardingNavigation = ({
  currentStep,
  hasSurvey,
  canNavigateToStep,
  onStepChange,
  onBack,
  onNext,
  isSubmitting = false,
  showBackButton = false,
  nextButtonLabel,
  showFinishAnimation = false,
  showOrganizationStep = true,
}: OnboardingNavigationProps) => {
  const { t } = useTranslation();

  return (
    <div className="sticky bottom-0 left-0 right-0 bg-background/80 backdrop-blur-md border-t border-border shadow-lg z-10">
      <div className="container max-w-6xl mx-auto">
        <div className="grid grid-cols-3 items-center py-4 px-4 sm:px-8">
          {/* Botão Voltar */}
          <div className="flex justify-start">
            {showBackButton && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="group flex items-center gap-2 transition-all hover:scale-105 text-sm text-primary font-semibold whitespace-nowrap"
              >
                ← {t("auth.onboarding.buttons.back")}
              </button>
            )}
          </div>

          {/* Indicadores de step */}
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => onStepChange('profile')}
              disabled={!canNavigateToStep('profile')}
              className="group flex flex-col items-center gap-1 sm:gap-2 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Step 1: Perfil"
            >
              <div className={`h-2 rounded-full transition-all ${
                currentStep === 'profile' 
                  ? 'bg-primary w-8 sm:w-12 shadow-lg shadow-primary/30' 
                  : 'bg-muted-foreground/30 w-6 sm:w-8 group-hover:bg-muted-foreground/50 group-hover:w-8 sm:group-hover:w-10'
              }`} />
              <span className={`text-[10px] sm:text-xs transition-colors hidden sm:inline ${
                currentStep === 'profile' ? 'text-primary font-medium' : 'text-muted-foreground'
              }`}>
                {t("auth.onboarding.steps.profile")}
              </span>
            </button>
            
            {hasSurvey && (
              <>
                <div className="h-px w-4 sm:w-8 bg-border" />
                
                <button
                  type="button"
                  onClick={() => canNavigateToStep('survey') && onStepChange('survey')}
                  disabled={!canNavigateToStep('survey')}
                  className="group flex flex-col items-center gap-1 sm:gap-2 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Step 2: Survey"
                >
                  <div className={`h-2 rounded-full transition-all ${
                    currentStep === 'survey' 
                      ? 'bg-primary w-8 sm:w-12 shadow-lg shadow-primary/30' 
                      : 'bg-muted-foreground/30 w-6 sm:w-8 group-hover:bg-muted-foreground/50 group-hover:w-8 sm:group-hover:w-10'
                  }`} />
                  <span className={`text-[10px] sm:text-xs transition-colors hidden sm:inline ${
                    currentStep === 'survey' ? 'text-primary font-medium' : 'text-muted-foreground'
                  }`}>
                    {t("auth.onboarding.steps.survey")}
                  </span>
                </button>
              </>
            )}
            
            {showOrganizationStep && (
              <>
                <div className="h-px w-4 sm:w-8 bg-border" />

                <button
                  type="button"
                  onClick={() => canNavigateToStep('organization') && onStepChange('organization')}
                  disabled={!canNavigateToStep('organization')}
                  className="group flex flex-col items-center gap-1 sm:gap-2 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={`Step ${hasSurvey ? '3' : '2'}: Organização`}
                >
                  <div className={`h-2 rounded-full transition-all ${
                    currentStep === 'organization'
                      ? 'bg-primary w-8 sm:w-12 shadow-lg shadow-primary/30'
                      : 'bg-muted-foreground/30 w-6 sm:w-8 group-hover:bg-muted-foreground/50 group-hover:w-8 sm:group-hover:w-10'
                  }`} />
                  <span className={`text-[10px] sm:text-xs transition-colors hidden sm:inline ${
                    currentStep === 'organization' ? 'text-primary font-medium' : 'text-muted-foreground'
                  }`}>
                    {t("auth.onboarding.steps.organization")}
                  </span>
                </button>
              </>
            )}
          </div>
          
          {/* Botão Próximo/Finalizar */}
          <div className="flex justify-end">
            {onNext && (
              <button
                type="button"
                onClick={onNext}
                disabled={isSubmitting}
                className="group relative flex items-center justify-center gap-2 transition-all hover:scale-105 text-sm text-primary font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px] px-4 py-2 rounded-md overflow-hidden"
              >
                {/* Animação de borda progressiva para o botão Finalizar */}
                {showFinishAnimation && !isSubmitting && (
                  <span className="absolute inset-0 rounded-md">
                    <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                      <rect 
                        x="1" 
                        y="1" 
                        width="calc(100% - 2px)" 
                        height="calc(100% - 2px)" 
                        rx="5" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                        strokeDasharray="600"
                        strokeDashoffset="600"
                        className="text-foreground animate-draw-border"
                      />
                    </svg>
                  </span>
                )}
                <span className="relative z-10">
                  {isSubmitting ? (
                    <LoadingSpinner className="h-3 w-3" size="sm" />
                  ) : (
                    nextButtonLabel || t("auth.onboarding.buttons.continue")
                  )}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
